import { UserRound } from "lucide-react";
import { Color } from "utils/theme";

// Generate initials from the name
function getInitials(name: string): string {
  if (!name) return "";
  // Split the name into parts by spaces
  const nameParts = name.split(" ").filter((word) => word.length > 0);

  // Handle the case with only one or two name parts
  if (nameParts.length === 1) {
    return nameParts[0][0].toUpperCase();
  } else if (nameParts.length === 2) {
    return nameParts[0][0].toUpperCase() + nameParts[1][0].toUpperCase();
  } else {
    // Handle the case with more than two name parts
    return (
      nameParts[0][0].toUpperCase() +
      nameParts[nameParts.length - 1][0].toUpperCase()
    );
  }
}

interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  name: string; // Name to generate initials if src is not provided
  size?: "xs" | "sm" | "md" | "lg"; // Size of the avatar
  anonymous?: boolean; // Show anonymous avatar
}

const Avatar: React.FC<AvatarProps> = ({
  name,
  size = "md",
  anonymous = false,
  ...props
}) => {
  // Size styles
  const sizeStyles = {
    xs: {
      container: { width: 30, height: 30, borderRadius: 99 },
      text: { fontSize: 10 },
    },
    sm: {
      container: { width: 32, height: 32, borderRadius: 99 },
      text: { fontSize: 12 },
    },
    md: {
      container: { width: 48, height: 48, borderRadius: 99 },
      text: { fontSize: 16 },
    },
    lg: {
      container: { width: 56, height: 56, borderRadius: 99 },
      text: { fontSize: 20 },
    },
  };

  if (anonymous) {
    return (
      <div
        {...props}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          backgroundColor: Color.Primary[800],
          color: Color.Primary[400],
          ...sizeStyles[size].container,
          ...props.style,
        }}
      >
        <UserRound className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div
      {...props}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        backgroundColor: Color.Primary[600],
        ...sizeStyles[size].container,
        ...props.style,
      }}
    >
      <p
        className="tracking-wider"
        style={{
          color: Color.White,
          fontWeight: "600",
          ...sizeStyles[size].text,
        }}
      >
        {getInitials(name)}
      </p>
    </div>
  );
};

export { Avatar };
